import Box from '@mui/material/Box';
import type { ReactElement } from 'react';

import { NUMERIC_FONT } from '../app/theme';

import { Amount, type AmountProps } from './Amount';
import { visuallyHidden } from './visually-hidden';

/** Props of {@link MoneyOrUnavailable}. */
export interface MoneyOrUnavailableProps {
  /** A contract `Money` (same shape as {@link AmountProps}), or null when the API could not compute it. */
  readonly value: Pick<AmountProps, 'amountMinor' | 'currency'> | null;
  /** What assistive tech reads instead of the dash ("Not available right now"), in the UI language. */
  readonly unavailableLabel: string;
}

/**
 * An amount, or « — » when the API could not compute it (specs/P10-risk-at-stop/spec.md R5): never
 * 0, which would read as "nothing at risk". The dash is hidden from assistive tech, which reads
 * `unavailableLabel` instead.
 */
export function MoneyOrUnavailable({
  value,
  unavailableLabel,
}: MoneyOrUnavailableProps): ReactElement {
  if (value !== null) return <Amount amountMinor={value.amountMinor} currency={value.currency} />;
  return (
    <Box component="span" sx={{ fontFamily: NUMERIC_FONT }}>
      {/* Literal, not a catalogue text: a typographic placeholder, the same in FR and EN, and never read aloud (the label below is). */}
      <span aria-hidden="true">—</span>
      <Box component="span" sx={visuallyHidden}>
        {unavailableLabel}
      </Box>
    </Box>
  );
}
